import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  mdiCreditCardOutline,
  mdiBackupRestore,
  mdiExport,
  mdiBriefcaseVariantOutline,
} from '@mdi/js'
import type { User } from '@racepointenergy/swc/scs/types'

import routes from 'app/routes'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import MiniCompanySelector from 'components/CompanySelector/MiniCompanySelector'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { hasSubscriptionPermissionSelector } from 'features/auth/permissions/selectors'
import { UserImgContainer } from 'components/UserItems'
import { currentUserSelector } from 'features/auth/login/selectors'
import { logOutThunk } from 'features/auth/login/slice'

import { accountMenuMessages } from '../messages'
import { StyleHeaderMenuSubTitle } from '../styles'
import {
  AccountMenuAvatarWrap,
  StyledAccountMenu,
  StyledAccountMenuIcon,
  StyledAccountMenuLink,
  StyledAccountMenuLogout,
} from './styles'

interface AccountMenuNavProps {
  onLinkClick?: () => void
}

function AccountMenuNav(props: AccountMenuNavProps) {
  const { onLinkClick } = props
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const currentUser: User = useAppSelector(currentUserSelector)
  const hasSubscriptionPermission = useAppSelector(
    hasSubscriptionPermissionSelector
  )

  const handleLogout = useCallback(() => {
    dispatch(logOutThunk())
  }, [dispatch])

  return (
    <StyledAccountMenu>
      <StyleHeaderMenuSubTitle>
        <FormattedMessage {...accountMenuMessages.company} />
      </StyleHeaderMenuSubTitle>
      <MiniCompanySelector onItemClick={onLinkClick} />

      <StyledAccountMenuLink
        exact
        to={routes.aboutCompany(rootDomainId)}
        onClick={onLinkClick}
      >
        <StyledAccountMenuIcon path={mdiBriefcaseVariantOutline} size={1} />
        {formatMessage(accountMenuMessages.aboutCompany)}
      </StyledAccountMenuLink>

      {hasSubscriptionPermission && (
        <>
          <StyledAccountMenuLink
            exact
            to={routes.paymentMethods(rootDomainId)}
            onClick={onLinkClick}
          >
            <StyledAccountMenuIcon path={mdiCreditCardOutline} size={1} />
            {formatMessage(accountMenuMessages.paymentMethods)}
          </StyledAccountMenuLink>

          <StyledAccountMenuLink
            exact
            to={routes.billingHistory(rootDomainId)}
            onClick={onLinkClick}
          >
            <StyledAccountMenuIcon path={mdiBackupRestore} size={1} />
            {formatMessage(accountMenuMessages.billingHistory)}
          </StyledAccountMenuLink>
        </>
      )}

      <StyleHeaderMenuSubTitle>{currentUser.email}</StyleHeaderMenuSubTitle>
      <StyledAccountMenuLink
        exact
        to={routes.accountProfile(rootDomainId)}
        onClick={onLinkClick}
      >
        <AccountMenuAvatarWrap>
          <UserImgContainer
            size={24}
            profilePicUrl={currentUser.profilePicUrl}
          />
        </AccountMenuAvatarWrap>
        {formatMessage(accountMenuMessages.accountProfile)}
      </StyledAccountMenuLink>
      <StyledAccountMenuLogout type="button" onClick={handleLogout}>
        <StyledAccountMenuIcon path={mdiExport} size={1} />
        {formatMessage(accountMenuMessages.logOut)}
      </StyledAccountMenuLogout>
    </StyledAccountMenu>
  )
}

export default AccountMenuNav
